import React, { useContext, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Heading3 } from '@/components/ui/typography'
import { isErrorSilent } from 'api/silentErrors'
import { ApiResponse } from 'App'
import { Modal } from 'components/CustomModal'
import { Select } from 'components/Select'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { Toggle } from 'components/Toggle/Toggle'
import { ResponseError, useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'

import { FormBaseButtonContainer, FormBaseRow } from '../../../../components/Form/Form.styles'
import { FormFieldBlock, FormFieldLabel } from '../../../Transactions/Transactions.styles'
import { FormFieldContainer } from './Client.styles'
import { Affiliate, SelectAffiliate } from './SelectAffiliate'

import styles from './ClientAssignAffiliateModal.module.scss'

interface Props {
  updateClient: () => void
  onClose(): void
  clientId: string
}

export const ClientAssignAffiliateModal: React.FC<Props> = ({
  updateClient,
  onClose,
  clientId,
}) => {
  const [selectAffiliateModal, setSelectAffiliateModal] = useState(false)
  const [ignoreChecks, setIgnoreChecks] = useState(false)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const setToast = useContext(ToastContext)

  const [affiliate, setAffiliate] = useState<Affiliate>()
  const [error, setError] = useState('')

  const [loading, setLoading] = useState(false)
  const onSelectAffiliate = (data: Affiliate) => {
    setError('')
    setAffiliate(data)
    setSelectAffiliateModal(false)
  }

  const handleToggleChange = () => {
    if (affiliate?.id) {
      setIgnoreChecks(!ignoreChecks)
    }
  }

  const onSubmit = async () => {
    try {
      setLoading(true)
      if (!affiliate) return
      await clientsApiClient.updateClientAffiliates({
        clientId,
        affiliateId: affiliate.id,
        ignoreChecks,
      })
      setToast(successToast('Affiliate Assigned'))
      updateClient()
      onClose()
    } catch (e) {
      if (e instanceof ResponseError && e?.response?.message) {
        const err = e?.response as ApiResponse
        if (isErrorSilent(err)) {
          if (err.response?.data?.message) setError(err.response?.data?.message)
        }
      }
      return
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {selectAffiliateModal && (
        <Modal
          width={1300}
          visible={selectAffiliateModal}
          onClose={() => setSelectAffiliateModal(false)}
        >
          <SelectAffiliate onChange={onSelectAffiliate} />
        </Modal>
      )}
      <Modal width={1024} height={968} visible={true} onClose={onClose}>
        <div className={styles.container}>
          <Heading3>Choose Affiliate</Heading3>
          <form>
            <FormFieldBlock>
              <FormBaseRow className={styles.affiliateWrapper}>
                <FormFieldLabel>Affiliate Code</FormFieldLabel>
                <FormFieldContainer>
                  <Select
                    className={styles.select}
                    error={error}
                    labelInValue={true}
                    placeholder='Choose Affiliate'
                    fullWidth
                    open={false}
                    value={affiliate?.externalId}
                    onFocus={() => setSelectAffiliateModal(true)}
                  />
                  {error && <div className={styles.error}>{error}</div>}
                </FormFieldContainer>
              </FormBaseRow>
              <FormBaseRow className={styles.affiliateWrapper}>
                <FormFieldLabel>Ignore Checks</FormFieldLabel>
                <FormFieldContainer>
                  <Toggle
                    disabled={!affiliate?.id}
                    name=''
                    value={ignoreChecks}
                    onChange={handleToggleChange}
                  />
                </FormFieldContainer>
              </FormBaseRow>
            </FormFieldBlock>
            <FormBaseButtonContainer
              style={{ justifyContent: 'center', marginTop: '2rem', gap: '1rem' }}
            >
              <Button key='button' type='button' onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={onSubmit}
                key='submit'
                type='button'
                disabled={!affiliate?.id || loading}
              >
                Submit
              </Button>
            </FormBaseButtonContainer>
          </form>
        </div>
      </Modal>
    </>
  )
}
